import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnDestroy, OnInit, Type, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@core/auth/auth.service';
import { LayoutDrawerComponent } from '@shared/components/page-layout/drawers/layout-drawer.component';
import { PageLayoutService } from '@shared/components/page-layout/page-layout.service';
import { BankAccountDrawerData, ParentDrawerRequest } from '@shared/components/page-layout/page-layout.types';
import { RecaptchaSecureParams } from '@shared/models/domain/custom-views.model';
import { UserBankAccount } from '@shared/models/domain/domain.model';
import { PageRequest } from '@shared/models/request/page-request.model';
import { UserBankAccountRequest } from '@shared/models/request/user-bank-account-request.model';
import { BillingErrorResponse } from '@shared/models/response/billing-error-response.model';
import { SelectOption } from '@shared/models/shared/select-option.model';
import { AlertService } from '@shared/services/alert.service';
import { BankAccountService } from '@shared/services/bank-account.service';
import { ReCaptchaV3Service } from '@core/recaptcha/recaptcha.service';
import { catchError, EMPTY, mergeMap, pairwise, startWith, Subject, takeUntil, tap } from 'rxjs';

/**
 * Gateway-agnostic bank account drawer behavior (sidemark assignment, nickname, default flag,
 * token fetch, persistence). Gateway-specific UI/tokenization is implemented by subclasses.
 * Never instantiated directly - decorated (matching LayoutDrawerComponent's own shape) only
 * because Angular requires an explicit Component/Directive decorator on any class in an
 * inheritance chain that uses DI/lifecycle hooks, and a Directive cannot extend a Component.
 */
@Component({ changeDetection: ChangeDetectionStrategy.Eager,
 template: '' })
export abstract class BankAccountDrawerBaseComponent extends LayoutDrawerComponent implements OnInit, OnDestroy {
    protected formBuilder = inject(UntypedFormBuilder);
    protected layoutService = inject(PageLayoutService);
    protected bankAccountService = inject(BankAccountService);
    protected changeDetectorRef = inject(ChangeDetectorRef);
    protected authService = inject(AuthService);
    protected reCaptchaV3Service = inject(ReCaptchaV3Service);
    protected alertService = inject(AlertService);
    public dialog = inject(MatDialog);
    private readonly destroyRef = inject(DestroyRef);
    declare drawer: ParentDrawerRequest<BankAccountDrawerData>;
    bankAccountForm = this.formBuilder.group({
        nickname: ['', []],
        setAsDefault: [false],
        projectIds: [[] as string[]],
        updateJobs: [false],
        updateProjects: [false]
    });

    busy = false;
    isCustomer = false;
    isEditMode = false;
    userBankAccount: UserBankAccount;
    sideMarkOptions: SelectOption[] = [];
    setSideMarkIds: string[] = [];
    showSideMark = false;
    unsubscribeAll: Subject<void> = new Subject<void>();

    /** Set from generateTokenReference() before the client-token fetch; undefined for gateways that don't need it (e.g. Braintree). */
    protected tokenReference: string | undefined;

    constructor() {
        super();
        this.title = 'Bank Account Details';
    }

    get bankAccountControls() {
        return this.bankAccountForm.controls;
    }

    ngOnInit(): void {
        this.isCustomer = this.drawer?.data.isCustomer;
        this.userBankAccount = this.drawer?.data?.userBankAccount;
        this.isEditMode = !!this.userBankAccount;
        const projects = this.drawer?.data.projects;
        this.sideMarkOptions = projects?.map(p => ({ value: p.id, label: p.name })) || [];

        if (this.userBankAccount?.externalBankAccountIdNonce) {
            this.setSideMarkIds = projects?.filter(p => p?.bankAccountId === this.userBankAccount.id).map(p => p.id) || [];
        }

        if (this.sideMarkOptions.length) {
            this.sideMarkOptions.unshift({ value: '', label: 'All Sidemarks' });
        }

        this.setupSidemarkListeners();
        this.setupGatewayFormControls(this.isEditMode);

        if (this.isEditMode) {
            const allSidemarkPresent = this.setSideMarkIds?.length === this.sideMarkOptions?.length - 1;
            const projectIdsValue = allSidemarkPresent ? ['', ...this.setSideMarkIds] : this.setSideMarkIds;

            this.bankAccountForm.patchValue({
                nickname: this.userBankAccount.nickname || '',
                projectIds: projectIdsValue,
                updateProjects: !!this.setSideMarkIds?.length
            });
            this.showSideMark = !!this.setSideMarkIds?.length;

            // Only nickname and sidemark assignment can be edited - disable everything else,
            // including whatever gateway-specific controls setupGatewayFormControls just added.
            Object.keys(this.bankAccountForm.controls).forEach(key => {
                if (!['nickname', 'projectIds', 'updateProjects', 'updateJobs'].includes(key)) {
                    this.bankAccountForm.controls[key].disable();
                }
            });
            this.changeDetectorRef.detectChanges();
            return;
        }

        this.busy = true;
        this.requestClientToken();
    }

    ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    /**
     * Fetches a client token and initializes the gateway widget. Extracted so a subclass can
     * re-run it after a retryable save error to mount a fresh widget against a newly generated
     * reference (mirrors CreditCardDrawerBaseComponent.requestClientToken()).
     */
    protected requestClientToken(): void {
        this.tokenReference = this.generateTokenReference();

        if (this.authService?.accessToken) {
            this.bankAccountService.getClientToken(this.tokenReference).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: (token) => this.initializeGateway(token),
                error: (error: HttpErrorResponse) => this.handleTokenError(error)
            });
        } else {
            this.reCaptchaV3Service.execute('bank_account')
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    mergeMap((recaptchaToken) => {
                        const secureParams: RecaptchaSecureParams = { recaptchaToken };
                        if (this.tokenReference) {
                            secureParams.reference = this.tokenReference;
                        }
                        return this.bankAccountService.getClientTokenSecure(secureParams).pipe(
                            tap((token) => this.initializeGateway(token)),
                            catchError((error: HttpErrorResponse) => {
                                this.handleTokenError(error);
                                return EMPTY;
                            }),
                        );
                    }),
                    catchError(() => {
                        this.busy = false;
                        this.changeDetectorRef.detectChanges();
                        this.alertService.errorToast('Verification Error', 'Security verification failed (reCaptcha). Please refresh the page and try again.');
                        return EMPTY;
                    }),
                )
                .subscribe();
        }
    }

    private setupSidemarkListeners(): void {
        this.bankAccountForm.controls.updateProjects.valueChanges
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe((value) => {
                this.showSideMark = value as boolean;
                this.changeDetectorRef.markForCheck();
            });

        this.bankAccountForm.controls.projectIds.valueChanges
            .pipe(
                startWith([] as string[]),
                pairwise<string[]>(),
                takeUntil(this.unsubscribeAll),
            )
            .subscribe(([prev, next]) => {
                const nextHasAll = next?.includes('');
                const prevHasAll = prev?.includes('');

                if (nextHasAll && !prevHasAll) {
                    this.bankAccountForm.controls.projectIds.setValue(
                        this.sideMarkOptions.map(s => s.value as string),
                        { emitEvent: false }
                    );
                } else if (nextHasAll && next.length < this.sideMarkOptions.length) {
                    this.bankAccountForm.controls.projectIds.setValue(
                        next.filter(n => n !== ''),
                        { emitEvent: false }
                    );
                } else if (!nextHasAll && prevHasAll && next.length === this.sideMarkOptions.length - 1) {
                    const nonRemovableSidemarks = this.sideMarkOptions
                        .filter(opt => this.isDisableSideMarkOption(String(opt.value)))
                        .map(opt => String(opt.value));
                    this.bankAccountForm.controls.projectIds.setValue(nonRemovableSidemarks, { emitEvent: false });
                }
            });
    }

    onCancel(): void {
        this.layoutService.closeParentDrawer();
    }

    onSubmit(): void {
        if (this.isEditMode) {
            this.handleEditBankAccount();
            return;
        }
        this.submitNewCard();
    }

    isDisableSideMarkOption(value: string): boolean {
        if (!value && this.setSideMarkIds?.length === this.sideMarkOptions?.length - 1) return true;
        return this.setSideMarkIds.includes(value);
    }

    /**
     * The concrete class rendered (Braintree or JPMorgan), so events emitted from shared
     * base-class logic identify themselves the same way `CustomBankAccountDrawerComponent`
     * resolves for whichever gateway is active in this build.
     */
    protected get drawerComponentType(): Type<LayoutDrawerComponent> {
        return this.constructor as Type<LayoutDrawerComponent>;
    }

    protected persistNewBankAccount(fields: { externalBankAccountIdNonce: string; accountType?: number; ownershipType?: number }): void {
        const request: Partial<UserBankAccountRequest> = {
            ...fields,
            userId: this.drawer?.data?.userId,
            nickname: this.bankAccountForm.controls.nickname.value as string,
            defaultAccount: this.bankAccountForm.controls.setAsDefault.value as boolean,
            updateJobs: this.bankAccountForm.controls.updateJobs.value as boolean,
            updateProjects: this.bankAccountForm.controls.updateProjects.value as boolean,
            projectIds: (this.bankAccountForm.controls.projectIds.value as string[])?.filter(p => p !== '')
        };

        const bankAccount = request as UserBankAccountRequest;
        if (this.authService?.accessToken) {
            this.bankAccountService.customCreate(bankAccount)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    mergeMap((res) => {
                        if (this.drawer?.data?.noObservableEmit) {
                            this.closeBankAccountDrawerWithResponse(res);
                            return EMPTY;
                        }
                        const pageRequest = new PageRequest();
                        pageRequest.currentPage = 1;
                        pageRequest.itemsPerPage = 1000;
                        pageRequest.sort = 'created';
                        pageRequest.sortDir = 'desc';
                        if (!this.isCustomer) {
                            pageRequest.find = {};
                            pageRequest.find['userId'] = this.drawer?.data?.userId;
                        }
                        return this.bankAccountService.get(pageRequest).pipe(
                            tap(() => this.closeBankAccountDrawerWithResponse(res)),
                            catchError(() => EMPTY),
                        );
                    }),
                    catchError((error: HttpErrorResponse) => {
                        this.busy = false;
                        this.handleSaveError(error);
                        this.changeDetectorRef.detectChanges();
                        return EMPTY;
                    }),
                )
                .subscribe();
        } else {
            this.onCancel();
            this.layoutService.setParentDrawerSubject(bankAccount);
        }
        this.changeDetectorRef.detectChanges();
    }

    private closeBankAccountDrawerWithResponse(res: UserBankAccount): void {
        this.bankAccountForm.reset();
        this.layoutService.closeParentDrawer();
        this.layoutService.setParentDrawerSubject({
            component: this.drawerComponentType,
            model: res
        });
        this.changeDetectorRef.markForCheck();
    }

    private handleEditBankAccount(): void {
        this.busy = true;
        this.changeDetectorRef.detectChanges();

        const updateRequest: Partial<UserBankAccountRequest> = {
            id: this.userBankAccount.id,
            nickname: (this.bankAccountForm.value as Pick<UserBankAccountRequest, 'nickname'>).nickname,
            externalBankAccountIdNonce: this.userBankAccount.externalBankAccountIdNonce,
            userId: this.userBankAccount.userId,
            updateJobs: this.bankAccountForm.controls.updateJobs.value as boolean,
            updateProjects: this.bankAccountForm.controls.updateProjects.value as boolean,
            projectIds: (this.bankAccountForm.controls.projectIds.value as string[])?.filter(p => p !== '')
        };

        this.bankAccountService.customUpdate(this.userBankAccount.id, updateRequest as UserBankAccountRequest)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.alertService.successToast('Success', 'Bank account updated successfully');
                    this.layoutService.closeParentDrawer();
                    this.layoutService.setParentDrawerSubject({
                        component: this.drawerComponentType,
                        model: res
                    });
                    this.busy = false;
                    this.changeDetectorRef.detectChanges();
                },
                error: (error: HttpErrorResponse) => {
                    this.busy = false;
                    this.handleSaveError(error);
                    this.changeDetectorRef.detectChanges();
                }
            });
    }

    /**
     * Backend billing errors return HTTP 422 with { code, message } (Braintree and JPMorgan
     * alike). Default: surface the server-provided message, or a generic fallback. A gateway
     * overrides this to special-case its own named codes.
     */
    protected handleSaveError(error: HttpErrorResponse): void {
        const body = error?.error as BillingErrorResponse | undefined;
        this.alertService.errorToast('Error', body?.message || 'Failed to process bank account information. Please try again.');
    }

    /** Client-token fetch failed (e.g. missing/invalid reference). Same { code, message } shape as save errors. */
    protected handleTokenError(error: HttpErrorResponse): void {
        this.busy = false;
        this.handleSaveError(error);
        this.changeDetectorRef.detectChanges();
    }

    /**
     * Reference passed as ?reference= to the client-token endpoint and echoed back as
     * externalBankAccountIdNonce when the account is saved, so the backend can correlate the
     * request - same contract as the JPMorgan credit-card drawer. Gateways that don't need this
     * leave the default no-op.
     */
    protected generateTokenReference(): string | undefined {
        return undefined;
    }

    /** Add/remove gateway-specific form controls; required only when adding a new account (not editing). */
    protected abstract setupGatewayFormControls(isEditMode: boolean): void;

    /** Called once a client token has been fetched from the backend. */
    protected abstract initializeGateway(token: string): void;

    /** Called from onSubmit() when adding a new account (not editing). */
    protected abstract submitNewCard(): void;
}
